import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { CURRENCIES } from 'utils/constants';
import { cipherID } from './idCipher';

dayjs.extend(advancedFormat);

export const formatBytes = (a, b = 2) => {
  if (a === 0) return '0 Bytes';
  const c = b < 0 ? 0 : b;
  const d = Math.floor(Math.log(a) / Math.log(1024));
  return (
    // eslint-disable-next-line no-restricted-properties
    `${parseFloat((a / Math.pow(1024, d)).toFixed(c))
    } ${
      ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'][d]}`
  );
};

export function imagePickSize(image, size) {
  const { [size]: imageSize } = image.resolutions;
  return imageSize ? imageSize.path : null;
}

export const isJsonValid = (jsonData) => {
  try {
    JSON.parse(jsonData);
    return true;
  } catch (error) {
    return false;
  }
};

export const setVarFromJSON = (json) => {
  try {
    return JSON.parse(json);
  } catch (error) {
    return {};
  }
};

export const saveFormData = (formData) => {
  localStorage.setItem('formData', JSON.stringify(formData));
  return true;
};

export const getFormData = () => {
  const formData = localStorage.getItem('formData');
  if (isJsonValid(formData)) return JSON.parse(formData);
  return {};
};

export const getSubmissionCategory = (category) => {
  const SUBMISSION_CATEGORIES = ['Real Wedding', 'Styled Shoot'];
  const SUBMISSION_TYPES = {
    real_wedding: 'Real Wedding',
    styled_shoot: 'Styled Shoot',
  };

  if (SUBMISSION_CATEGORIES.includes(category)) return category;
  return SUBMISSION_TYPES[category] || SUBMISSION_CATEGORIES[0];
};

export const getSession = () => {
  const sessionInfo = localStorage.getItem('sessionInfo');
  return isJsonValid(sessionInfo) ? JSON.parse(sessionInfo) : {};
};

export const setSession = (session) => {
  try {
    localStorage.setItem('sessionInfo', JSON.stringify(session));
    return true;
  } catch (e) {
    return false;
  }
};

export const dateformat = (str) => {
  if (!str) return '';
  const datearr = str.split('-');
  const m = dayjs(`${datearr[0]}-{${datearr[1]}}-${datearr[2]}`).format('YYYY-MM-DD');
  return dayjs(m).format('MMMM Do YYYY');
};

export const getLocalTimeString = (dateTime) => {
  if (!dateTime) return '';
  return new Date(dateTime).toLocaleTimeString('en-us', {
    year: 'numeric', month: 'short',
    day: '2-digit', hour: '2-digit', minute: '2-digit',
  });
};

export const getLocalDateString = (dateTime) => {
  if (!dateTime) return '';
  return new Date(dateTime).toLocaleDateString('en-us', {
    year: 'numeric', month: 'short',
    day: '2-digit',
  });
};

/* eslint-disable no-bitwise */
export const generateUUID = () => {
  const pattern = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx';
  return pattern.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
};
/* eslint-enable no-bitwise */

export const isUrlValid = (str) => {
  let websiteStr = str;
  if (str.indexOf('www.') > -1) {
    websiteStr = str.substring(str.indexOf('www.') + 4);
  }

  const urlPatternString = [
    '^(https?:\\/\\/)?',                // Match the protocol (http or https), optional
    '([a-zA-Z0-9-_]+\\.)+[a-zA-Z]{2,}', // Match the domain
    '(\\/[a-zA-Z0-9-_%.@]*)*',           // Match the path, optional
    '(\\?[a-zA-Z0-9-_%.]+=[a-zA-Z0-9-_%.]*(?:&[a-zA-Z0-9-_%.]+=[a-zA-Z0-9-_%.]*)*)?',// Match query parameters, optional
    '(#[a-zA-Z0-9-_%.\\/]+)?$',            // Match the fragment identifier, optional, allowing slashes
  ].join('');

  // Create the regex pattern using the RegExp constructor
  const urlPattern = new RegExp(urlPatternString);
  return !!urlPattern.test(websiteStr);
};

export const isEmailValid = (email) => {
  const re = /\S+@\S+\.\S+/;

  return re.test(email);
};

export const isPhoneValid = (phone) => {
  const re = /^(?=.*[0-9])[- +()0-9]+$/i;
  return re.test(phone);
};

const IMAGE_EXTENSION = ['jpg', 'jpeg', 'png'];

export const isFileImageExtension = (filename, extensions = IMAGE_EXTENSION) => {
  // #TODO: this doesn't work for urls with query strings.
  const m = filename.match(/\.([^.]+)$/);
  return extensions.indexOf(m && m[1]) !== -1;
};


export const copyToClipboard = (string) => {
  navigator.clipboard.writeText(string);
};

export const centsToDollars = (cents, currency = 'USD', localeOverride, excludeCents = false) => {
  // Define decimal places for special currencies
  const decimalPlaces = {
    BHD: 3,
    JOD: 3,
    KWD: 3,
    OMR: 3,
    CLF: 4,
    BTC: 8,
  };

  // Each currency carries its canonical formatting locale (e.g. EUR -> de-DE, JPY -> ja-JP)
  // so grouping/decimal separators match the currency. localeOverride is an escape hatch.
  const locale = localeOverride || CURRENCIES.find(c => c.value === currency)?.locale || 'en-US';

  const params = {
    style: 'currency',
    currency,
    minimumFractionDigits: decimalPlaces[currency] || 2,
    maximumFractionDigits: decimalPlaces[currency] || 2,
  };

  if (excludeCents) {
    params.minimumFractionDigits = 0;
    params.maximumFractionDigits = 0;
  }

  const formatter = new Intl.NumberFormat(locale, params);

  const dollarAmount = excludeCents ? Math.floor(cents / 100) : cents / 100;

  return formatter.format(dollarAmount);
};

// Port number formatter from original app to match displayed values.
// from webapp/aisleplanner/common/js/services.js
export const legacyNumberFormatter = (
  amount,
  {
    decimalPlaces = 2,
    decimalSeparator = '.',
    groupPlaces = 3,
    groupSeparator = ',',
    negativeInParens = false,
    divisor = 1,
    hideDecimalZeros = true,
    suffix,
  },
) => {
  let newAmount = amount;
  const isNegative = amount < 0;
  if (isNegative){
    newAmount *= -1;
  }
  let value = newAmount / divisor;
  let wholeValue;
  let decimalValue;
  let scale;
  if (decimalPlaces === 0) {
    wholeValue = Math.round(value);
    decimalValue = '';
  } else {
    scale = 10 ** decimalPlaces;
    value = Math.round(value * scale);
    wholeValue = Math.floor(value / scale);
    decimalValue = value % scale;
  }
  let whole = String(wholeValue);
  let decimal = String(decimalValue);
  while (decimal.length < decimalPlaces){
    decimal = `0${decimal}`;
  }
  if (hideDecimalZeros){
    decimal = decimal.replace(/0+$/,'');
  }
  let amountString;
  for (let i = whole.length - groupPlaces; i > 0; i -= groupPlaces){
    whole = whole.substr(0, i) + groupSeparator + whole.substr(i);
  }
  if (decimal.length > 0){
    amountString = whole + decimalSeparator + decimal;
  }else{
    amountString = whole;
  }
  if (isNegative){
    if (negativeInParens){
      amountString = `(${amountString})`;
    }else{
      amountString = `-${amountString}`;
    }
  }
  if (suffix !== undefined){
    amountString += suffix;
  }
  return amountString;
};

// Port currency converter from original app to match displayed values.
// from webapp/aisleplanner/common/js/services.js
export const legacyConvertCurrency = (
  amount,
  currencyType,
  options,
) => {
  const currency = CURRENCIES.find(_currency => currencyType === _currency.value);
  const opts = { ...options };
  if (opts.useUniqueFormat === undefined) opts.useUniqueFormat = false;
  if (opts.format === undefined) opts.format = opts.useUniqueFormat ? currency.unique_format : currency.format;
  if (opts.decimalPlaces === undefined) opts.decimalPlaces = currency.decimal_places;
  if (opts.decimalSeparator === undefined) opts.decimalSeparator = currency.decimal_sep;
  if (opts.groupPlaces === undefined) opts.groupPlaces = 3;
  if (opts.groupSeparator === undefined) opts.groupSeparator = currency.group_sep;
  if (opts.negativeInParens === undefined) opts.negativeInParens = true;
  if (opts.divisor === undefined) opts.divisor = 100;
  if (opts.hideDecimalZeros === undefined) opts.hideDecimalZeros = false;
  const amountString = legacyNumberFormatter(amount, opts);
  if (amountString.length > 1 && amountString.charAt(0) === '(' && amountString.charAt(amountString.length - 1)){
    return `(${opts.format.replace('%s', amountString.substr(1, amountString.length - 2))})`;
  }
  return opts.format.replace('%s', amountString);
};

export const convertCurrency = (value, currencyValue) => {
  const currency = CURRENCIES.find(item => item.value === currencyValue);
  const digits = currency ? currency.decimal_places : 2;
  const symbol = currency ? currency.symbol : '$';

  if (value >= 0) {
    return `${symbol}${(value / 10 ** digits).toFixed(digits)}`;
  }

  return `-${symbol}${(-1 * value / 10 ** digits).toFixed(digits)}`;
};

/**
 * Handles various types of input that represents amount and converts it to a cents value.
 * @param {*} value - Could potentially be a string or number.
 * @returns number - the number of cents
 */
export const toCents = (value) => {
  // Remove currency symbols and commas
  const cleaned = String(value).replace(/[$,]/g, '');
  const number = parseFloat(cleaned);
  if (Number.isNaN(number)) {
    throw new Error('Invalid monetary value');
  }

  return Math.round(number * 100);
};

export const getPreviewUrl = (id, isTemplate = false) => {

  const encryptedID = cipherID(id);
  if (isTemplate) {
    return`${window.location.origin}/app/v2/note-template-viewer/${encryptedID}`;
  }
  return`${window.location.origin}/app/v2/note-viewer/${encryptedID}`;

};

/* eslint-disable */
/**
 * Converts the keys of an object or array of objects to camel case instead of underscore separated.
 * @param {Object|Array} obj - the object or array of objects to convert.
 * @param {Object} settings - Configurable settings
 * @param {array} settings.ignore - array of keys to ignore
 * @returns {Object|Array|null} - Returns the converted object or array of objects.
 */
export const objToCamelCase = (obj, settings) => {
  const { ignore } = settings || { ignore: [] };
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => objToCamelCase(item, settings));
  }

  const camelCaseObj = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const camelCaseKey = key.replace(/([-_][a-z])/g, group =>
        group.toUpperCase()
          .replace('-', '')
          .replace('_', '')
      );
      if (ignore.includes(camelCaseKey)) {
        camelCaseObj[camelCaseKey] = obj[key];
      } else {
        camelCaseObj[camelCaseKey] = objToCamelCase(obj[key], settings);
      }
    }
  }
  return camelCaseObj;
};

/**
 * Converts the keys of an object or array of objects to snake case instead of camelcase.
 * @param {Object|Array} obj - the object or array of objects to convert.
 * @returns {Object|Array|null} - Returns the converted object or array of objects.
 */
export const objToSnake = (obj) => {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map(item => objToSnake(item));
  }

  // Handle objects
  const snakeObj = {};

  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      // Convert camelCase to snake_case
      const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);

      // Recursively convert nested objects
      snakeObj[snakeKey] = objToSnake(obj[key]);
    }
  }

  return snakeObj;
}
/* eslint-enable */
